import { useMemo, useState } from 'react';
import { X, Plus, Check } from 'lucide-react';
import type {
  MenuItem,
  MenuItemVariant,
  MenuItemAddOn,
  MenuItemCustomizationGroup,
} from '../../App';
import { useLocale } from '../../lib/locale';

interface CustomizeItemModalProps {
  item: MenuItem;
  onClose: () => void;
  onAddToCart: (
    item: MenuItem,
    selectedVariant: MenuItemVariant | null,
    selectedAddOns: MenuItemAddOn[],
    specialInstructions: string
  ) => void;
}

const buildInitialSelection = (groups: MenuItemCustomizationGroup[] = []) =>
  groups.reduce<Record<string, string[]>>((acc, group) => {
    const defaultOptions = (group.options || [])
      .filter((option) => option.isDefault)
      .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));

    if (defaultOptions.length > 0) {
      acc[group.id] = defaultOptions.map((option) => option.id);
      return acc;
    }

    if (group.inputType === 'single' && group.isRequired && group.options?.length) {
      acc[group.id] = [group.options[0].id];
      return acc;
    }

    acc[group.id] = [];
    return acc;
  }, {});

export function CustomizeItemModal({ item, onClose, onAddToCart }: CustomizeItemModalProps) {
  const { formatMoney } = useLocale();
  const customizationGroups = (item.customizationGroups || []).slice().sort((a, b) => a.sortOrder - b.sortOrder);

  const [selectedVariant, setSelectedVariant] = useState<MenuItemVariant | null>(
    item.variants?.find((v) => v.isDefault) || item.variants?.[0] || null
  );
  const [selectedMenuAddOns, setSelectedMenuAddOns] = useState<MenuItemAddOn[]>([]);
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [selectedCustomizationOptions, setSelectedCustomizationOptions] = useState<Record<string, string[]>>(
    () => buildInitialSelection(customizationGroups)
  );

  const toggleMenuAddOn = (addOn: MenuItemAddOn) => {
    setSelectedMenuAddOns((prev) => {
      const existing = prev.find((a) => a.id === addOn.id);
      if (existing) return prev.filter((a) => a.id !== addOn.id);
      return [...prev, addOn];
    });
  };

  const toggleCustomizationOption = (group: MenuItemCustomizationGroup, optionId: string) => {
    setSelectedCustomizationOptions((previous) => {
      const current = previous[group.id] || [];
      if (group.inputType === 'single') {
        return { ...previous, [group.id]: [optionId] };
      }

      const exists = current.includes(optionId);
      const next = exists ? current.filter((id) => id !== optionId) : [...current, optionId];
      const maxSelect = group.maxSelect ?? null;
      if (!exists && maxSelect !== null && next.length > maxSelect) {
        return previous;
      }
      return { ...previous, [group.id]: next };
    });
  };

  const selectedCustomizationAddOns = useMemo(() => {
    return customizationGroups.flatMap((group) =>
      (group.options || [])
        .filter((option) => (selectedCustomizationOptions[group.id] || []).includes(option.id))
        .map((option) => ({
          id: option.id,
          name: option.name,
          price: option.price,
          category: option.category || group.displayName,
          source: 'customization' as const,
          customizationOptionId: option.id,
          groupName: group.displayName,
        }))
    );
  }, [customizationGroups, selectedCustomizationOptions]);

  const selectedAddOns = useMemo(
    () => {
      return [
        ...selectedMenuAddOns,
        ...selectedCustomizationAddOns,
      ];
    },
    [selectedCustomizationAddOns, selectedMenuAddOns]
  );

  const basePrice = selectedVariant?.price || item.price;
  const addOnsTotal = selectedAddOns.reduce((sum, addOn) => sum + addOn.price, 0);
  const totalPrice = basePrice + addOnsTotal;

  const handleAddToCart = () => {
    onAddToCart(item, selectedVariant, selectedAddOns, specialInstructions);
    onClose();
  };

  const addOnsByCategory =
    item.addOns?.reduce((acc, addOn) => {
      if (!acc[addOn.category]) acc[addOn.category] = [];
      acc[addOn.category].push(addOn);
      return acc;
    }, {} as Record<string, MenuItemAddOn[]>) || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 p-3 sm:p-5" onClick={onClose}>
      <div
        className="w-full max-w-3xl max-h-[94vh] overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between border-b border-gray-100 bg-white px-4 py-3">
          <div className="flex-1 pr-4">
            <h2 className="mb-0.5 text-lg text-gray-900">{item.name}</h2>
            <p className="line-clamp-2 text-sm text-gray-600">
              {item.description || 'Customize this item before adding to your cart.'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 transition-colors hover:bg-gray-100"
            aria-label="Close customization modal"
          >
            <X className="h-5 w-5 text-gray-600" />
          </button>
        </div>

        <div className="max-h-[calc(92vh-138px)] space-y-4 overflow-y-auto px-4 py-4">
          {item.variants && item.variants.length > 0 && (
            <div>
              <h3 className="mb-2 text-base text-gray-900">Portion Size</h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {item.variants.map((variant) => (
                  <button
                    key={variant.id}
                    onClick={() => setSelectedVariant(variant)}
                    className={`w-full rounded-xl border-2 px-3 py-2.5 transition-all ${
                      selectedVariant?.id === variant.id
                        ? 'border-[#60A5FA] bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-gray-300'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span
                        className={`text-sm ${
                          selectedVariant?.id === variant.id ? 'font-medium text-gray-900' : 'text-gray-700'
                        }`}
                      >
                        {variant.name}
                      </span>
                      <div
                        className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${
                          selectedVariant?.id === variant.id
                            ? 'border-[#60A5FA] bg-[#60A5FA]'
                            : 'border-gray-300 bg-white'
                        }`}
                      >
                        {selectedVariant?.id === variant.id && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                      </div>
                    </div>
                    <p className="mt-1 text-right text-sm text-gray-700">{formatMoney(variant.price)}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          {customizationGroups.map((group) => (
            <div key={group.id}>
              <h3 className="mb-2 text-base text-gray-900">{group.displayName}</h3>
              <div className={`grid gap-2 ${group.inputType === 'single' ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>
                {group.options.map((option) => {
                  const isSelected = (selectedCustomizationOptions[group.id] || []).includes(option.id);
                  return (
                    <button
                      key={option.id}
                      onClick={() => toggleCustomizationOption(group, option.id)}
                      className={`flex items-center justify-between rounded-xl border-2 px-3 py-2.5 text-sm transition-all ${
                        isSelected
                          ? 'border-[#60A5FA] bg-blue-50 text-gray-900'
                          : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <span>{option.name}</span>
                      <span>{option.price > 0 ? `+Rs. ${option.price}` : group.inputType === 'single' ? 'Included' : ''}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          {Object.entries(addOnsByCategory).map(([category, addOns]) => (
            <div key={category}>
              <h3 className="mb-2 text-base text-gray-900">{category}</h3>
              <div className="space-y-2">
                {addOns.map((addOn) => {
                  const isSelected = selectedMenuAddOns.some((a) => a.id === addOn.id);
                  return (
                    <button
                      key={addOn.id}
                      onClick={() => toggleMenuAddOn(addOn)}
                      className={`flex w-full items-center justify-between rounded-xl border-2 px-3 py-2.5 transition-all ${
                        isSelected
                          ? 'border-[#60A5FA] bg-blue-50'
                          : 'border-gray-200 bg-white hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-5 w-5 items-center justify-center rounded-md border-2 ${
                            isSelected ? 'border-[#60A5FA] bg-[#60A5FA]' : 'border-gray-300 bg-white'
                          }`}
                        >
                          {isSelected && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                        </div>
                        <span className={`text-sm ${isSelected ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                          {addOn.name}
                        </span>
                      </div>
                      {addOn.price !== 0 && (
                        <span className={`text-sm ${isSelected ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                          +Rs. {addOn.price}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div>
            <h3 className="mb-2 text-base text-gray-900">Special Instructions (Optional)</h3>
            <textarea
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Any specific requests? e.g., Less oil, No onions..."
              className="w-full rounded-xl border-2 border-gray-200 px-3 py-2.5 text-sm focus:border-[#60A5FA] focus:outline-none focus:ring-0"
              rows={3}
              maxLength={200}
            />
            <p className="mt-1.5 text-xs text-gray-500">{specialInstructions.length}/200 characters</p>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-100 bg-white px-4 py-3">
          <div className="text-sm text-gray-600">
            <p>Selected options: {selectedAddOns.length}</p>
            <p className="text-base text-gray-900">Total: {formatMoney(totalPrice)}</p>
          </div>
          <button
            onClick={handleAddToCart}
            className="min-w-[180px] rounded-xl bg-[#60A5FA] px-5 py-3 text-white shadow-md transition-all hover:bg-[#3B82F6] hover:shadow-lg"
          >
            <span className="flex items-center gap-2 font-semibold">
              <Plus className="h-5 w-5" strokeWidth={3} />
              Add Item
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
